import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    FiSearch,
    FiChevronLeft,
    FiChevronRight,
    FiChevronDown,
    FiChevronUp,
    FiInfo,
    FiX,
    FiFileText,
    FiFile,
    FiCheckCircle,
    FiLoader,
    FiAlertCircle,
    FiThumbsUp,
    FiThumbsDown,
    FiXCircle,
    FiFilter,
    FiMessageSquare,
} from "react-icons/fi";
import Swal from "sweetalert2";
import {
    questionnairesApi,
    assessmentsApi,
    crosswalkApi,
    questionChatApi,
} from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import QuestionChat from "../../components/QuestionChat";

const confidenceLevels = ["Very Low", "Low", "Moderate", "High", "Very High"];

// Normalize the crosswalk rationale field, which the backend may return as:
//   - a plain string (legacy / local fallback), or
//   - a structured object { text, details: { sourceEvidence, targetNeed,
//     controlAlignment, orgRelevance, caveats } }.
// The downstream UI parses a labeled string, so flatten the object into that form.
const normalizeRationale = (r) => {
    if (r == null) return "";
    if (typeof r === "string") return r;
    if (typeof r !== "object") return String(r);
    const parts = [];
    const text = r.rationale || r.text;
    if (text && typeof text === "string") parts.push(`Rationale: ${text}`);
    const d = r.details;
    if (d && typeof d === "object") {
        const {
            source_evidence,
            sourceEvidence,
            target_need,
            targetNeed,
            control_alignment,
            controlAlignment,
            org_relevance,
            orgRelevance,
            caveats,
        } = d;
        const sourceEvidenceVal = source_evidence || sourceEvidence;
        const targetNeedVal = target_need || targetNeed;
        const controlAlignmentVal = control_alignment || controlAlignment;
        const orgRelevanceVal = org_relevance || orgRelevance;
        if (sourceEvidence) parts.push(`Source evidence: ${sourceEvidence}`);
        if (targetNeed) parts.push(`Target need: ${targetNeed}`);
        if (controlAlignment)
            parts.push(`Control objective alignment: ${controlAlignment}`);
        if (orgRelevance) parts.push(`Mapping basis: ${orgRelevance}`);
        if (caveats) parts.push(`Caveats: ${caveats}`);
    }
    return parts.length ? parts.join(" ") : text || "";
};

const normalizeEvidenceDocuments = (proposal) => {
    const enrichments = [
        proposal?.documentEnrichment,
        proposal?.document_enrichment,
        ...(proposal?.documentEnrichments || []),
        ...(proposal?.document_enrichments || []),
    ].filter(Boolean);

    const documents = [
        ...(proposal?.evidenceDocuments || []),
        ...(proposal?.evidence_documents || []),
        ...enrichments.flatMap((e) => e.evidence || []),
    ];

    const seen = new Set();
    return documents
        .map((d) => ({
            chunk_id: d.chunkId || d.chunk_id || null,
            doc_title:
                d.docTitle || d.doc_title || d.fileName || d.file_name || null,
            blob_url: d.blobUrl || d.blob_url || null,
            ref_doc_id: d.refDocId || d.ref_doc_id || null,
            chunk_ordinal: d.chunkOrdinal || d.chunk_ordinal || null,
            section_title: d.sectionTitle || d.section_title || null,
            snippet: d.snippet || null,
            retrieval_score: d.retrievalScore ?? d.retrieval_score ?? null,
        }))
        .filter((d) => d.doc_title || d.blob_url || d.snippet)
        .filter((d) => {
            const key =
                d.chunk_id ||
                `${d.blob_url || d.doc_title || "doc"}:${d.snippet || d.chunk_ordinal || ""}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
};

// Map confidence score (1-5) to label
const confidenceScoreToLabel = (score) => {
    if (score >= 1 && score <= 5) return confidenceLevels[score - 1];
    return "Moderate";
};

// Map API question data to the shape the UI expects, merging answer data when available
const mapApiQuestion = (apiQ, answer) => {
    const options = apiQ.answerOptions?.length
        ? apiQ.answerOptions.map((o) => o.value)
        : ["Yes", "No", "NA"];

    // Determine selected option index from the answer's selectedOption text
    let selectedOption = null;
    if (answer?.selectedOption) {
        const idx = options.findIndex(
            (o) => o.toLowerCase() === answer.selectedOption.toLowerCase(),
        );
        if (idx >= 0) selectedOption = idx;
    }

    return {
        id: apiQ.questionId,
        text: apiQ.questionText || "",
        options,
        answerOptions: apiQ.answerOptions || [], // full options with ids, for crosswalk alignment matching
        selectedOption,
        confidence: answer
            ? confidenceScoreToLabel(answer.confidenceScore)
            : null,
        showExplanation: false,
        rationale: [
            "Prior Answer - NIST SC-28 - mentions AES & rotation",
            "NIST SC-28 - ISO A 10.1 - cryptographic controls alignment",
        ],
        crossWalkTags: apiQ.controlIdentifier
            ? [apiQ.controlIdentifier]
            : ["NIST SC-28", "ISO 27001 A 10.1", "Vendor Risk Q2"],
        reviewerNotes: answer?.answerReasons || "",
        reviewStatus: answer?.reviewStatus || null,
        answerId: answer?.answerId || null,
        additionalInfo: answer?.additionalInfo || null,
        additionalInfoUrl: answer?.additionalinfoUrl || null,
        detailedReview: {
            description:
                apiQ.instructions ||
                "Data at rest is encrypted using AES-256; keys stored in HSM; rotation every 90 days.",
            provenance:
                'Derived from prior answer "Vendor Risk Q2 2024 rgb(90, 90, 10)", aligned with NIST 800-53 SC-28 and ISO 27001 A.10.1',
            crossWalk:
                "Prior Answer - NIST SC-28 (AES-256 & rotation) - ISO A.10.1 (crypto controls)",
        },
    };
};

// ─── Component ─────────────────────────────────────────────────────────────

const UserQuestionnaires = () => {
    const navigate = useNavigate();
    const { assessmentId } = useParams();
    const scrollRef = useRef(null);
    const [searchTerm, setSearchTerm] = useState("");

    // Query (per-question chat) filter: "all" | "open" | "resolved" | "any".
    const [queryFilter, setQueryFilter] = useState("all");
    // questionId → 1 (open) | 2 (resolved). Questions with no thread are absent.
    const [chatStatusMap, setChatStatusMap] = useState({});

    // Questionnaires state
    const [questionnaires, setQuestionnaires] = useState([]);
    const [loadingQnrs, setLoadingQnrs] = useState(true);
    const [qnrError, setQnrError] = useState(null);

    // Active questionnaire & questions
    const [activeQnr, setActiveQnr] = useState(null);
    const [questionsMap, setQuestionsMap] = useState({});
    const [loadingQuestions, setLoadingQuestions] = useState(false);
    const fetchedQnrIds = useRef(new Set());

    // Crosswalk data states
    const [proposal, setProposal] = useState([]);
    const [sourceAnswer, setSourceAnswer] = useState([]);
    const [crosswalkJobIds, setCrosswalkJobIds] = useState({});
    const [crosswalkLoading, setCrosswalkLoading] = useState(false);

    // Two-way acceptance state
    const [acceptanceStatus, setAcceptanceStatus] = useState({
        reviewerAccepted: false,
        userAccepted: false,
        userRejected: false,
        bothAccepted: false,
    });
    const [accepting, setAccepting] = useState(false);
    const [rejecting, setRejecting] = useState(false);

    // Fetch questionnaires for this assessment using the CountInfo API so
    // the card values (answered/reviewed/lastUpdated) come directly from the API.
    const { user } = useAuth();
    const fetchQuestionnaires = useCallback(async () => {
        if (!assessmentId || !user?.id) return;
        setLoadingQnrs(true);
        setQnrError(null);
        try {
            // Fetch both dashboard data and questionnaire details (for crosswalkJobId)
            const [resp, qnrDetails] = await Promise.all([
                assessmentsApi.CountInfo(user.id),
                questionnairesApi.getAll(1, 100, null, null, assessmentId),
            ]);

            const dashboard = Array.isArray(resp) ? resp : resp.data || [];
            const assessment = dashboard.find(
                (a) => String(a.id) === String(assessmentId),
            );
            const items = Array.isArray(assessment?.questionnaires)
                ? assessment.questionnaires
                : [];

            // Get crosswalkJobIds from questionnaire details
            const detailItems =
                qnrDetails.items ||
                qnrDetails.data ||
                (Array.isArray(qnrDetails) ? qnrDetails : []);
            const jobIdMap = {};
            detailItems.forEach((q) => {
                if (q.crosswalkJobId) {
                    // Store with both string and original key for flexible matching
                    jobIdMap[q.questionnaireId] = q.crosswalkJobId;
                    jobIdMap[String(q.questionnaireId)] = q.crosswalkJobId;
                }
            });
            console.log("QUESTIONNAIRES: JobIdMap:", jobIdMap);
            console.log(
                "QUESTIONNAIRES: Dashboard items:",
                items.map((i) => ({ id: i.id, name: i.name })),
            );
            console.log(
                "QUESTIONNAIRES: Detail items:",
                detailItems.map((d) => ({
                    id: d.questionnaireId,
                    crosswalkJobId: d.crosswalkJobId,
                })),
            );
            setCrosswalkJobIds(jobIdMap);

            // Map API questionnaire shape to the UI's expected fields
            const mapped = items.map((q) => {
                const jobId = jobIdMap[q.id] || jobIdMap[String(q.id)] || null;
                console.log(
                    `QUESTIONNAIRES: Mapping ${q.id} (${q.name}) with crosswalkJobId: ${jobId}`,
                );
                return {
                    questionnaireId: q.id,
                    questionnaireName: q.name,
                    answered: q.answered ?? 0,
                    totalQuestion: q.totalQuestion ?? 0,
                    reviewed: q.reviewed ?? 0,
                    lastUpdated: q.lastUpdated ?? q.createdDate ?? null,
                    crosswalkJobId: jobId,
                };
            });

            setQuestionnaires(mapped);

            // Prefetch questions for ALL questionnaires to determine which have approved questions
            const allQuestionsMap = {};
            await Promise.all(
                mapped.map(async (qnr) => {
                    try {
                        const [apiQuestions, answersData] = await Promise.all([
                            questionnairesApi.getQuestions(qnr.questionnaireId),
                            assessmentId
                                ? questionnairesApi.getAnswers(
                                    qnr.questionnaireId,
                                    assessmentId,
                                )
                                : Promise.resolve([]),
                        ]);
                        const answers = Array.isArray(answersData)
                            ? answersData
                            : [];
                        const answersByQuestion = {};
                        for (const ans of answers) {
                            answersByQuestion[ans.questionId] = ans;
                        }
                        const mappedQs = (
                            Array.isArray(apiQuestions) ? apiQuestions : []
                        ).map((q) =>
                            mapApiQuestion(
                                q,
                                answersByQuestion[q.questionId] || null,
                            ),
                        );
                        allQuestionsMap[qnr.questionnaireId] = mappedQs;
                        fetchedQnrIds.current.add(qnr.questionnaireId);
                    } catch (err) {
                        console.error(
                            "Failed to prefetch questions for",
                            qnr.questionnaireId,
                            err,
                        );
                        allQuestionsMap[qnr.questionnaireId] = [];
                    }
                }),
            );
            setQuestionsMap(allQuestionsMap);

            // Set active questionnaire to the first one with approved questions
            const firstApproved = mapped.find((qnr) => {
                const qs = allQuestionsMap[qnr.questionnaireId] || [];
                return qs.some((q) => q.reviewStatus === "approved");
            });

            let activeQnrId = null;
            if (firstApproved) {
                activeQnrId = firstApproved.questionnaireId;
                setActiveQnr(firstApproved.questionnaireId);
            } else if (mapped.length > 0) {
                activeQnrId = mapped[0].questionnaireId;
                setActiveQnr(mapped[0].questionnaireId);
            }

            // NOTE: Crosswalk data is fetched via useEffect when activeQnr changes
        } catch (err) {
            console.error(
                "Failed to fetch questionnaires from dashboard API:",
                err,
            );
            setQnrError("Failed to load questionnaires. Please try again.");
        } finally {
            setLoadingQnrs(false);
        }
    }, [assessmentId, user?.id]);

    useEffect(() => {
        fetchQuestionnaires();
    }, [fetchQuestionnaires]);

    // Fetch acceptance status for this assessment
    const fetchAcceptanceStatus = useCallback(async () => {
        if (!assessmentId || !user?.id) return;
        try {
            const status = await assessmentsApi.getAcceptanceStatus(
                assessmentId,
                user.id,
            );
            setAcceptanceStatus({
                reviewerAccepted: status.reviewerAccepted ?? false,
                userAccepted: status.userAccepted ?? false,
                userRejected: status.userRejected ?? false,
                bothAccepted: status.bothAccepted ?? false,
            });
        } catch (err) {
            console.error("Failed to fetch acceptance status:", err);
        }
    }, [assessmentId, user?.id]);

    useEffect(() => {
        fetchAcceptanceStatus();
    }, [fetchAcceptanceStatus]);

    const handleUserAccept = async () => {
        const result = await Swal.fire({
            title: "Accept Assessment?",
            text: "By clicking Accept, you confirm that you have reviewed and agree with all approved answers in this assessment.",
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#ff7817",
            cancelButtonColor: "#919191",
            confirmButtonText: "Accept",
            cancelButtonText: "Cancel",
        });

        if (!result.isConfirmed) return;

        setAccepting(true);
        try {
            const userEmail = user?.email || null;
            const res = await assessmentsApi.userAccept(assessmentId, userEmail);
            await fetchAcceptanceStatus();
            if (res?.emailsSent === false) {
                Swal.fire({
                    title: "Accepted — email not sent",
                    text: "Your acceptance was recorded, but the acceptance emails/report could not be sent (email service error). Please ask the team to check the email configuration.",
                    icon: "warning",
                    confirmButtonColor: "#ff7817",
                });
            } else {
                Swal.fire({
                    title: "ACCEPTED!",
                    text: acceptanceStatus.reviewerAccepted
                        ? "Both parties have accepted. Acceptance emails with PDF report are being sent."
                        : "Your acceptance has been recorded. The report will be sent once the reviewer also accepts.",
                    icon: "success",
                    confirmButtonColor: "#ff7817",
                });
            }
        } catch (err) {
            console.error("Failed to accept assessment:", err);
            Swal.fire({
                title: "ERROR",
                text: "Failed to record your acceptance. Please try again.",
                icon: "error",
                confirmButtonColor: "#ff7817",
            });
        } finally {
            setAccepting(false);
        }
    };

    const handleUserReject = async () => {
        const result = await Swal.fire({
            title: "Reject Assessment?",
            text: "By clicking Reject, you indicate that you are not satisfied with the reviewed answers. The reviewer will be notified.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#e53e3e",
            cancelButtonColor: "#919191",
            confirmButtonText: "Yes, Reject",
            cancelButtonText: "Cancel",
        });

        if (!result.isConfirmed) return;

        setRejecting(true);
        try {
            const userEmail = user?.email || null;
            const res = await assessmentsApi.userReject(assessmentId, userEmail);
            await fetchAcceptanceStatus();
            if (res?.emailsSent === false) {
                Swal.fire({
                    title: "Rejected — email not sent",
                    text: "Your rejection was recorded, but the notification email could not be sent (email service error). Please ask the team to check the email configuration.",
                    icon: "warning",
                    confirmButtonColor: "#ff7817",
                });
            } else {
                Swal.fire({
                    title: "REJECTED",
                    text: "Your rejection has been recorded. The reviewer has been notified.",
                    icon: "info",
                    confirmButtonColor: "#ff7817",
                });
            }
        } catch (err) {
            console.error("Failed to reject assessment:", err);
            Swal.fire({
                title: "ERROR",
                text: "Failed to record your rejection. Please try again.",
                icon: "error",
                confirmButtonColor: "#ff7817",
            });
        } finally {
            setRejecting(false);
        }
    };

    // Fetch questions when active questionnaire changes
    const fetchQuestions = useCallback(
        async (qnrId) => {
            if (!qnrId) return;
            // If already fetched, skip
            if (fetchedQnrIds.current.has(qnrId)) return;
            fetchedQnrIds.current.add(qnrId);
            setLoadingQuestions(true);
            try {
                // Materialize any crosswalk proposals into answers before loading them so the
                // auto-prefilled selections show up. Idempotent and best-effort.
                if (assessmentId) {
                    await crosswalkApi
                        .syncProposals(assessmentId, qnrId)
                        .catch(() => { });
                }

                // Fetch questions and answers in parallel
                const [apiQuestions, answersData] = await Promise.all([
                    questionnairesApi.getQuestions(qnrId),
                    assessmentId
                        ? questionnairesApi.getAnswers(qnrId, assessmentId)
                        : Promise.resolve([]),
                ]);
                const answers = Array.isArray(answersData) ? answersData : [];
                // Build a lookup: questionId -> answer
                const answersByQuestion = {};
                for (const ans of answers) {
                    answersByQuestion[ans.questionId] = ans;
                }
                const mapped = (
                    Array.isArray(apiQuestions) ? apiQuestions : []
                ).map((q) =>
                    mapApiQuestion(q, answersByQuestion[q.questionId] || null),
                );
                setQuestionsMap((prev) => ({ ...prev, [qnrId]: mapped }));
            } catch (err) {
                console.error("Failed to fetch questions:", err);
                setQuestionsMap((prev) => ({ ...prev, [qnrId]: [] }));
            } finally {
                setLoadingQuestions(false);
            }
        },
        [assessmentId],
    );

    // Fetch crosswalk data for displaying proposal and source answer info
    // Fetch crosswalk proposals directly from DB. Identical logic for reviewer,
    // user, and admin paths; does not depend on the upstream job cache TTL.
    const fetchCrosswalkData = useCallback(
        async (qnrId) => {
            if (!qnrId || !assessmentId) return;

            setCrosswalkLoading(true);
            try {
                const response = await crosswalkApi.getProposals(
                    assessmentId,
                    qnrId,
                );
                const dbProposals = response?.proposals || [];

                if (dbProposals.length === 0) {
                    setProposal([]);
                    setSourceAnswer([]);
                    return;
                }

                // Keep only proposals that have a structured nested rationale (with details),
                // matching the reviewer behavior. Plain-string fallback rationales are dropped.
                const nestedRationaleProposals = dbProposals.filter((p) => {
                    let r = p.rationale;
                    if (typeof r === "string") {
                        try {
                            r = JSON.parse(r);
                        } catch (e) {
                            return false;
                        }
                    }
                    return r && typeof r === "object" && r.details;
                });

                const formattedProposals = nestedRationaleProposals.map((p) => {
                    // Parse rationale: may arrive as a JS object OR as a JSON string
                    let rationaleObj = p.rationale;
                    if (typeof rationaleObj === "string") {
                        try {
                            rationaleObj = JSON.parse(rationaleObj);
                        } catch (e) {
                            /* plain string */
                        }
                    }
                    const isObj =
                        rationaleObj && typeof rationaleObj === "object";
                    return {
                        target_question_id: p.targetQuestionId,
                        source_answer_id: p.sourceAnswerId,
                        source_question_id: p.sourceQuestionId,
                        source_answer_text: p.sourceAnswerText,
                        confidence: p.confidence,
                        rationale: isObj
                            ? rationaleObj.rationale || rationaleObj.text || ""
                            : typeof p.rationale === "string"
                                ? p.rationale
                                : normalizeRationale(p.rationale),
                        rationale_details:
                            isObj && rationaleObj.details
                                ? rationaleObj.details
                                : null,
                        scores: {
                            confidence: p.confidence,
                            bm25: p.scoreBm25 || 0,
                        },
                        option_alignments: (p.optionAlignments || []).map(
                            (o) => ({
                                target_option_id: o.targetOptionId,
                                source_option_text: o.sourceOptionText,
                                alignment_score: o.similarityScore || 0,
                                is_primary: o.isPrimarySuggestion,
                            }),
                        ),
                        document_enrichment:
                            p.documentEnrichment ||
                            p.document_enrichment ||
                            null,
                        document_enrichments:
                            p.documentEnrichments ||
                            p.document_enrichments ||
                            [],
                        // Documents and cited chunks the crosswalk grounded this proposal on.
                        evidence_documents: normalizeEvidenceDocuments(p),
                    };
                });
                setProposal(formattedProposals);

                const seenIds = new Set();
                const syntheticSourceAnswers = [];
                dbProposals.forEach((p) => {
                    if (p.sourceAnswerId && !seenIds.has(p.sourceAnswerId)) {
                        seenIds.add(p.sourceAnswerId);
                        syntheticSourceAnswers.push({
                            answer_id: p.sourceAnswerId,
                            question_id: p.sourceQuestionId,
                            question_text: p.sourceQuestionText || null,
                            answer_text: p.sourceAnswerText,
                            questionnaire_id: p.sourceQuestionnaireId || null,
                            questionnaire_name:
                                p.sourceQuestionnaireName || null,
                            assessment_id: p.sourceAssessmentId || null,
                            assessment_description:
                                p.sourceAssessmentDescription || null,
                        });
                    }
                });
                setSourceAnswer(syntheticSourceAnswers);
            } catch (err) {
                console.error("Failed to fetch crosswalk data:", err);
            } finally {
                setCrosswalkLoading(false);
            }
        },
        [assessmentId],
    );

    // Effect to fetch questions and crosswalk data when active questionnaire changes
    useEffect(() => {
        if (activeQnr) {
            fetchQuestions(activeQnr);
            // Only fetch crosswalk if questionnaires are loaded (for switching after initial load)
            if (questionnaires.length > 0) {
                fetchCrosswalkData(activeQnr);
            }
        }
    }, [activeQnr, fetchQuestions, fetchCrosswalkData, questionnaires.length]);

    // NOTE: We intentionally do NOT overwrite questionnaire card fields with
    // computed values from preloaded questions. The card should display only
    // API-provided values (answered, totalQuestion, reviewed, lastUpdated).

    const questions = questionsMap[activeQnr] || [];

    // Filter to only show approved questions, then apply the query filter and
    // the search term.
    const filteredQuestions = React.useMemo(() => {
        // First filter to only approved questions
        let approvedQuestions = questions.filter(
            (q) => q.reviewStatus === "approved",
        );

        // Query filter: "all" = everything, "open"/"resolved" = threads in that
        // state, "any" = questions that have a thread at all (status 1 or 2).
        if (queryFilter !== "all") {
            approvedQuestions = approvedQuestions.filter((q) => {
                const status = chatStatusMap[q.id];
                if (queryFilter === "open") return status === 1;
                if (queryFilter === "resolved") return status === 2;
                return status === 1 || status === 2;
            });
        }

        const term = searchTerm.trim().toLowerCase();
        if (!term) return approvedQuestions;
        return approvedQuestions.filter((q) => {
            const haystack = [
                q.text,
                ...q.options,
                ...q.crossWalkTags,
                q.reviewerNotes,
            ]
                .join(" ")
                .toLowerCase();
            return haystack.includes(term);
        });
    }, [questions, searchTerm, queryFilter, chatStatusMap]);

    // Load the per-question query statuses for this assessment. Refreshed when a
    // chat panel closes so the filter reflects threads opened/accepted just now.
    const refreshChatStatuses = useCallback(async () => {
        if (!assessmentId) return;
        try {
            const rows = await questionChatApi.getThreadSummary(
                parseInt(assessmentId, 10),
            );
            const map = {};
            (Array.isArray(rows) ? rows : []).forEach((r) => {
                map[r.questionId] = r.status;
            });
            setChatStatusMap(map);
        } catch (err) {
            console.error("Failed to load query statuses", err);
        }
    }, [assessmentId]);

    useEffect(() => {
        refreshChatStatuses();
    }, [refreshChatStatuses]);

    // Count approved questions for each questionnaire
    const getApprovedCount = useCallback(
        (qnrId) => {
            const qnrQuestions = questionsMap[qnrId] || [];
            return qnrQuestions.filter((q) => q.reviewStatus === "approved")
                .length;
        },
        [questionsMap],
    );

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const questionsPerPage = 5;
    const totalPages = Math.ceil(filteredQuestions.length / questionsPerPage);
    const paginatedQuestions = filteredQuestions.slice(
        (currentPage - 1) * questionsPerPage,
        currentPage * questionsPerPage,
    );

    // Reset to page 1 when the search term or query filter changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, queryFilter]);

    const handleSwitchQnr = (qnrId) => {
        setActiveQnr(qnrId);
        setCurrentPage(1);
    };

    const toggleExplanation = (id) => {
        setQuestionsMap((prev) => {
            const current = prev[activeQnr] || [];
            return {
                ...prev,
                [activeQnr]: current.map((q) =>
                    q.id === id
                        ? { ...q, showExplanation: !q.showExplanation }
                        : q,
                ),
            };
        });
    };

    const scrollQnr = (dir) => {
        if (scrollRef.current) {
            scrollRef.current.scrollBy({ left: dir * 180, behavior: "smooth" });
        }
    };

    return (
        <div className="space-y-5 relative">
            {/* ─── Header: Questionnaires ─── */}
            <div className="bg-[#ffffff] cardStyle border border-[#919191]/30 p-5">
                <div className="flex items-center justify-between mb-4">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2">
                            <h2 className="text-xl font-bold text-[#0077b5]">
                                Questionnaires
                            </h2>
                            {crosswalkLoading && (
                                <span className="flex items-end gap-[3px] pb-[2px]">
                                    {[0, 1, 2].map((i) => (
                                        <span
                                            key={i}
                                            style={{
                                                animationDelay: `${i * 0.18}s`,
                                            }}
                                            className="block w-[6px] h-[6px] rounded-full bg-[#0077b5] animate-bounce"
                                        />
                                    ))}
                                </span>
                            )}
                        </div>
                        <nav className="text-sm mt-1 text-[#919191]">
                            <button
                                onClick={() => navigate("/user/assessments")}
                                className="text-[#0077b5] hover:underline font-medium p-0 mr-2"
                            >
                                Assessments
                            </button>
                            <span className="text-[#919191]">&gt;</span>
                            <span className="ml-2 text-[#919191] font-medium">
                                Questionnaires
                            </span>
                        </nav>
                    </div>
                    <div className="flex items-center gap-3">
                        {/* Query filter — filters the question list by chat thread status */}
                        <div className="flex items-stretch border border-[#c8dcea] text-xs overflow-hidden">
                            <span className="px-3 py-2 bg-[#f4f8fb] flex items-center gap-1.5 text-[#53545a] font-semibold border-r border-[#c8dcea] whitespace-nowrap">
                                <FiFilter className="text-[#0077b5]" />
                                Query
                            </span>
                            <select
                                value={queryFilter}
                                onChange={(e) => setQueryFilter(e.target.value)}
                                className="px-3 py-2 bg-white text-[#53545a] outline-none cursor-pointer appearance-none pr-7"
                            >
                                <option value="all">All Questions</option>
                                <option value="open">Open</option>
                                <option value="resolved">Resolved</option>
                                <option value="any">All Query</option>
                            </select>
                        </div>

                        {/* Search */}
                        <div className="flex items-center border border-[#919191]/40 px-3 py-2">
                            <FiSearch className="text-[#919191] mr-2" />
                            <input
                                type="text"
                                placeholder="Search questions..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="bg-transparent border-none outline-none text-sm w-40 text-[#53545a] placeholder-[#919191]"
                            />
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm("")}
                                    className="ml-1 text-[#919191] hover:text-[#53545a] transition-colors"
                                    title="Clear search"
                                >
                                    <FiX className="text-sm" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Loading questionnaires - show detailed skeleton similar to reviewer page */}
                {loadingQnrs && (
                    <div className="space-y-6">
                        <div className="flex items-center justify-between mb-4">
                            <div className="min-w-0">
                                <div className="w-48 h-6 bg-[#e6eef5]  animate-pulse mb-2" />
                                <div className="w-64 h-4 bg-[#e6eef5]  animate-pulse" />
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="w-28 h-10 bg-[#e6eef5] animate-pulse" />
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <div className="p-1 rounded-full text-[#919191] flex-shrink-0">
                                <div className="w-6 h-6 rounded-full bg-[#e6eef5] animate-pulse" />
                            </div>
                            <div className="flex gap-3 overflow-x-auto flex-1">
                                {Array.from({ length: 5 }).map((_, i) => (
                                    <div
                                        key={i}
                                        className="min-w-[170px] max-w-[190px] p-3 bg-white border border-[#e6eef5]"
                                    >
                                        <div className="w-32 h-4 bg-[#e6eef5]  animate-pulse mb-3" />
                                        <div className="w-full h-3 bg-[#e6eef5] animate-pulse mb-2" />
                                        <div className="w-full h-3 bg-[#e6eef5]  animate-pulse" />
                                    </div>
                                ))}
                            </div>
                            <div className="p-1 rounded-full text-[#919191] flex-shrink-0">
                                <div className="w-6 h-6 rounded-full bg-[#e6eef5] animate-pulse" />
                            </div>
                        </div>
                    </div>
                )}

                {/* Error loading questionnaires */}
                {qnrError && !loadingQnrs && (
                    <div className="flex items-center gap-3 mt-4 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700">
                        <FiAlertCircle className="text-lg flex-shrink-0" />
                        <span className="text-sm">{qnrError}</span>
                        <button
                            onClick={fetchQuestionnaires}
                            className="ml-auto text-sm font-medium underline hover:no-underline"
                        >
                            Retry
                        </button>
                    </div>
                )}

                {/* Scrollable questionnaire cards - only show if there are approved questionnaires */}
                {!loadingQnrs &&
                    !qnrError &&
                    questionnaires.length > 0 &&
                    questionnaires.filter(
                        (qnr) => getApprovedCount(qnr.questionnaireId) > 0,
                    ).length > 0 && (
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => scrollQnr(-1)}
                                className="p-1 rounded-full hover:bg-[#919191]/10 text-[#919191] flex-shrink-0"
                            >
                                <FiChevronLeft />
                            </button>
                            <div
                                ref={scrollRef}
                                className="flex gap-3 overflow-x-auto scrollbar-hide flex-1"
                                style={{ scrollbarWidth: "none" }}
                            >
                                {questionnaires
                                    .filter(
                                        (qnr) =>
                                            getApprovedCount(
                                                qnr.questionnaireId,
                                            ) > 0,
                                    )
                                    .map((qnr) => {
                                        const isActive =
                                            qnr.questionnaireId === activeQnr;
                                        // Use only API-provided counters for the card display
                                        const answered = qnr.answered ?? 0;
                                        const total = qnr.totalQuestion ?? 0;
                                        const isCompleted =
                                            total > 0 && answered >= total;
                                        const statusLabel = isCompleted
                                            ? "Completed"
                                            : "Progress";
                                        const progressPct =
                                            total > 0
                                                ? Math.min(
                                                    (answered / total) * 100,
                                                    100,
                                                )
                                                : 0;
                                        const qnrQuestions =
                                            questionsMap[qnr.questionnaireId] ||
                                            [];
                                        // Use only API-provided reviewed/totalQuestion for the card display
                                        const reviewedCount = qnr.reviewed ?? 0;
                                        const reviewTotal =
                                            qnr.totalQuestion ?? 0;
                                        const reviewPct =
                                            reviewTotal > 0
                                                ? Math.min(
                                                    Math.round(
                                                        (reviewedCount /
                                                            reviewTotal) *
                                                        100,
                                                    ),
                                                    100,
                                                )
                                                : 0;
                                        const formattedDate = qnr.lastUpdated
                                            ? new Date(
                                                qnr.lastUpdated,
                                            ).toLocaleDateString("en-US", {
                                                month: "short",
                                                day: "2-digit",
                                                year: "numeric",
                                            })
                                            : qnr.createdDate
                                                ? new Date(
                                                    qnr.createdDate,
                                                ).toLocaleDateString("en-US", {
                                                    month: "short",
                                                    day: "2-digit",
                                                    year: "numeric",
                                                })
                                                : null;
                                        return (
                                            <button
                                                key={qnr.questionnaireId}
                                                onClick={() =>
                                                    handleSwitchQnr(
                                                        qnr.questionnaireId,
                                                    )
                                                }
                                                className={`min-w-[170px] max-w-[190px] flex-shrink-0 border-2 p-3 text-left transition-all ${isActive
                                                        ? "border-accent-400 bg-accent-50 shadow-md"
                                                        : "border-blue-200 bg-white hover:border-blue-400 hover:shadow-sm"
                                                    }`}
                                            >
                                                {/* Dot + name */}
                                                <div className="flex items-center gap-2 mb-2">
                                                    <span
                                                        className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${isActive
                                                                ? "bg-accent-400"
                                                                : "bg-blue-500"
                                                            }`}
                                                    ></span>
                                                    <span
                                                        className={`text-xs font-semibold truncate ${isActive
                                                                ? "text-accent-500"
                                                                : "text-blue-900"
                                                            }`}
                                                    >
                                                        {qnr.questionnaireName}
                                                    </span>
                                                </div>
                                            </button>
                                        );
                                    })}
                            </div>
                            <button
                                onClick={() => scrollQnr(1)}
                                className="p-1 rounded-full hover:bg-[#919191]/10 text-[#919191] flex-shrink-0"
                            >
                                <FiChevronRight />
                            </button>
                        </div>
                    )}

                {questionnaires.filter(
                    (qnr) => getApprovedCount(qnr.questionnaireId) > 0,
                ).length > 0 && (
                        <p className="text-sm text-[#919191] mt-4">
                            Click each questionnaire card to view its questions and
                            answers — scroll down to see them. Once all
                            questionnaires are done, scroll down below to accept the
                            assessment — we will send you the final report by email.
                        </p>
                    )}

                {/* No questionnaires */}
                {!loadingQnrs && !qnrError && questionnaires.length === 0 && (
                    <div className="mt-4 p-4  bg-[#919191]/5 text-center text-[#919191]">
                        No questionnaires found for this assessment.
                    </div>
                )}
            </div>

            {/* ─── Questions Loading ─── */}
            {loadingQuestions && (
                <div className="space-y-4">
                    {Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="relative border-2  p-6">
                            <div className="flex items-start justify-between mb-3">
                                <div className="min-w-0">
                                    <div className="w-28 h-4 bg-[#e6eef5] rounded animate-pulse mb-2" />
                                    <div className="w-40 h-3 bg-[#e6eef5] rounded animate-pulse" />
                                </div>
                                <div className="w-20 h-8 bg-[#e6eef5] rounded animate-pulse" />
                            </div>

                            {/* Options skeleton */}
                            <div className="grid grid-cols-1 gap-3">
                                {Array.from({ length: 3 }).map((_, j) => (
                                    <div
                                        key={j}
                                        className="flex items-center gap-4"
                                    >
                                        <div className="w-4 h-4 rounded-full bg-[#e6eef5] animate-pulse" />
                                        <div className="w-40 h-3 bg-[#e6eef5] rounded animate-pulse" />
                                    </div>
                                ))}
                            </div>

                            {/* Confidence label skeleton */}
                            <div className="mt-4">
                                <div className="w-24 h-3 bg-[#e6eef5] rounded animate-pulse mb-2" />
                                <div className="flex items-center gap-3">
                                    {Array.from({ length: 5 }).map((_, k) => (
                                        <div
                                            key={k}
                                            className="w-14 h-3 bg-[#e6eef5] rounded animate-pulse"
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* Undo button skeleton bottom-left */}
                            <div className="mt-4">
                                <div className="w-20 h-8 bg-[#e6eef5] animate-pulse" />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* ─── Questions Section ─── */}
            {!loadingQuestions && (
                <div className="space-y-4">
                    {paginatedQuestions.map((q) => (
                        <UserQuestionCard
                            key={q.id}
                            question={q}
                            onToggleExplanation={() => toggleExplanation(q.id)}
                            proposal={proposal}
                            sourceAnswer={sourceAnswer}
                            activeQnr={activeQnr}
                            onChatStatusChange={refreshChatStatuses}
                        />
                    ))}
                    {!loadingQnrs && activeQnr && questions.length === 0 && (
                        <div className="p-4 bg-[#919191]/5 text-center text-[#919191]">
                            No questions found for this questionnaire.
                        </div>
                    )}
                    {!loadingQnrs &&
                        activeQnr &&
                        questions.length > 0 &&
                        filteredQuestions.length === 0 &&
                        !searchTerm && (
                            <div className="p-4 bg-orange-50 border border-orange-200 text-center text-orange-600">
                                <FiAlertCircle className="inline-block mr-2" />
                                No reviewed approved questions in this
                                questionnaire.
                            </div>
                        )}
                    {!loadingQnrs &&
                        activeQnr &&
                        questions.length > 0 &&
                        filteredQuestions.length === 0 &&
                        searchTerm && (
                            <div className="p-4 bg-[#919191]/5 text-center text-[#919191]">
                                No approved questions match &ldquo;{searchTerm}
                                &rdquo;.
                            </div>
                        )}
                </div>
            )}

            {/* ─── Pagination ─── */}
            {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 pt-2 pb-4">
                    <button
                        onClick={() =>
                            setCurrentPage((p) => Math.max(1, p - 1))
                        }
                        disabled={currentPage === 1}
                        className="w-9 h-9 flex items-center justify-center rounded-full border border-[#919191]/40 text-[#919191] hover:bg-[#919191]/10 disabled:opacity-40 transition-colors"
                    >
                        <FiChevronLeft className="text-sm" />
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                        (page) => (
                            <button
                                key={page}
                                onClick={() => setCurrentPage(page)}
                                className={`w-9 h-9 flex items-center justify-center rounded-full text-sm font-bold transition-all ${currentPage === page
                                        ? "bg-[#F47F2E] text-[#ffffff] shadow-md"
                                        : "border border-[#919191]/40 text-[#919191] hover:bg-[#919191]/10"
                                    }`}
                            >
                                {page}
                            </button>
                        ),
                    )}
                    {totalPages > 5 && (
                        <span className="text-[#919191] text-sm tracking-widest">
                            ···
                        </span>
                    )}
                    <button
                        onClick={() =>
                            setCurrentPage((p) => Math.min(totalPages, p + 1))
                        }
                        disabled={currentPage === totalPages}
                        className="w-9 h-9 flex items-center justify-center rounded-full border border-[#919191]/40 text-[#919191] hover:bg-[#919191]/10 disabled:opacity-40 transition-colors"
                    >
                        <FiChevronRight className="text-sm" />
                    </button>
                </div>
            )}

            {/* ─── Two-Way Acceptance Banner ─── */}
            {!loadingQnrs &&
                !loadingQuestions &&
                filteredQuestions.length > 0 &&
                (acceptanceStatus.reviewerAccepted ||
                    acceptanceStatus.userRejected) && (
                    <div className="flex items-center justify-center gap-3 py-4">
                        {acceptanceStatus.userAccepted ? (
                            <div className="flex items-center gap-2 px-10 py-3 rounded-full bg-green-100 text-green-700 text-sm font-semibold border border-green-300">
                                <FiCheckCircle className="text-sm" />
                                You have already accepted
                            </div>
                        ) : acceptanceStatus.userRejected &&
                            !acceptanceStatus.reviewerAccepted ? (
                            <div className="flex flex-col items-center gap-1">
                                {/* <div className="flex items-center gap-2 px-10 py-3 rounded-full bg-red-100 text-red-700 text-sm font-semibold border border-red-300">
                                    <FiXCircle className="text-sm" />
                                    You have rejected this assessment
                                </div> */}
                                <p className="text-xs text-[#919191] mt-1">
                                    The reviewer has been notified and will
                                    re-review. You can act again once they
                                    re-accept.
                                </p>
                            </div>
                        ) : (
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={handleUserAccept}
                                    disabled={accepting || rejecting}
                                    className="flex items-center gap-2 px-8 py-3 bg-[#F47F2E] text-white text-sm font-semibold shadow-md hover:bg-[#e06a1a] active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                                >
                                    {accepting ? (
                                        <FiLoader className="animate-spin text-sm" />
                                    ) : (
                                        <FiThumbsUp className="text-sm" />
                                    )}
                                    {accepting ? "Accepting..." : "Accept full questionnaire"}
                                </button>
                                {/* <button
                                    onClick={handleUserReject}
                                    disabled={accepting || rejecting}
                                    className="flex items-center gap-2 px-8 py-3 bg-white text-red-600 text-sm font-semibold shadow-md border-2 border-red-400 hover:bg-red-50 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                                >
                                    {rejecting ? (
                                        <FiLoader className="animate-spin text-sm" />
                                    ) : (
                                        <FiThumbsDown className="text-sm" />
                                    )}
                                    {rejecting ? "Rejecting..." : "Reject"}
                                </button> */}
                            </div>
                        )}
                    </div>
                )}
        </div>
    );
};

// ─── UserQuestionCard Sub-component ─────────────────────────────────────────

const UserQuestionCard = ({
    question,
    onToggleExplanation,
    proposal = [],
    sourceAnswer = [],
    activeQnr,
    onChatStatusChange,
}) => {
    const q = question;
    const { assessmentId } = useParams();
    const [showDetails, setShowDetails] = useState(false);
    const [detailTab, setDetailTab] = useState("Cross-Walk");
    const [showChat, setShowChat] = useState(false);
    const [chatMaximized, setChatMaximized] = useState(false);

    // Find crosswalk recommendation for this question
    const rec = proposal.find((r) => r.target_question_id === q.id);

    // Find source answer by answer_id (for the answer text)
    const sourceAns = rec
        ? sourceAnswer.find((sa) => sa.answer_id === rec.source_answer_id)
        : null;

    // Find source question data by source_question_id (for question text, assessment, questionnaire)
    const sourceQuestionData = rec
        ? sourceAnswer.find((sa) => sa.question_id === rec.source_question_id)
        : null;

    const handleOpenDocument = async () => {
        if (!q.answerId || !activeQnr) return;
        try {
            const result = await questionnairesApi.getDocumentUrl(
                activeQnr,
                q.answerId,
            );
            window.open(result.url, "_blank", "noopener,noreferrer");
        } catch (err) {
            console.error("Failed to open document", err);
        }
    };

    // Download a crosswalk evidence document via a short-lived SAS URL (signed to force download).
    // The raw blob URL is not publicly accessible. Triggers a direct download, no new tab.
    const openEvidenceDoc = async (blobUrl) => {
        try {
            const { url } = await crosswalkApi.getEvidenceDocumentUrl(blobUrl);
            const a = document.createElement("a");
            a.href = url;
            a.rel = "noopener";
            document.body.appendChild(a);
            a.click();
            a.remove();
        } catch (err) {
            console.error("Failed to download evidence document", err);
        }
    };

    // Resolve the crosswalk proposal's Questionnaire Intelligence match: the
    // primary (or highest scoring) option alignment, requiring ≥ 50% similarity.
    // Returns { optionId, percent } or null. Mirrors QuestionCard.js.
    const crosswalkMatch = (() => {
        const alignments = rec?.option_alignments || [];
        if (alignments.length === 0) return null;
        const primary =
            alignments.find((a) => a.is_primary) ||
            [...alignments].sort(
                (a, b) => (b.alignment_score || 0) - (a.alignment_score || 0),
            )[0];
        if (!primary) return null;
        const percent = Math.round((primary.alignment_score || 0) * 100);
        if (percent < 50) return null;
        return { optionId: primary.target_option_id, percent };
    })();

    // ─── View Query chat body (shared between slide-in panel & maximized modal) ──
    const closeChat = () => {
        setShowChat(false);
        setChatMaximized(false);
        // A thread may have been started or accepted — refresh the query filter data.
        onChatStatusChange?.();
    };

    const chatBody = (
        <QuestionChat
            assessmentId={assessmentId ? parseInt(assessmentId, 10) : null}
            questionId={q.id}
            answerId={q.answerId ?? null}
            questionText={q.text}
            senderRole="user"
            open={showChat}
            chatMaximized={chatMaximized}
            setChatMaximized={setChatMaximized}
            onClose={closeChat}
        />
    );

    return (
        <div
            className={` border-2 bg-[#ffffff] p-5 transition-all relative overflow-hidden shadow-sm ${q.reviewStatus === "approved"
                    ? "border-[ #0076b545]"
                    : q.reviewStatus === "rejected"
                        ? "border-[ #f4812e48]"
                        : "border-[ #f4812e44]/40"
                }`}
        >
            {/* Question header row */}
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-[#919191] flex items-center gap-1">
                        Question{" "}
                        <FiInfo
                            className="text-[#919191] text-xs"
                            title="Question info"
                        />
                    </span>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => setShowChat(true)}
                        className="text-xs font-semibold text-[#0077b5] hover:text-[#005a8f] flex items-center gap-1.5 flex-shrink-0 border border-[#0077b5] px-3 py-1.5 hover:bg-[#0077b5]/10 transition-colors"
                    >
                        <FiMessageSquare className="text-sm" />
                        View Query
                    </button>
                    <button
                        onClick={onToggleExplanation}
                        className="text-xs text-[#919191] hover:text-[#0077b5] flex items-center gap-1 flex-shrink-0 transition-colors"
                    >
                        {q.showExplanation
                            ? "Hide explanation"
                            : "Show explanation"}
                        {q.showExplanation ? (
                            <FiChevronUp className="text-xs" />
                        ) : (
                            <FiChevronDown className="text-xs" />
                        )}
                    </button>
                </div>
            </div>

            {/* Question text */}
            <p className="text-sm font-semibold text-[#0077b5] mt-2">
                {q.text}
            </p>

            {/* Answer options (read-only) */}
            <div className="mt-3 ml-2 space-y-1.5">
                {q.options.map((opt, idx) => {
                    const hasRealSelection = q.selectedOption != null;
                    const isSelected =
                        hasRealSelection && q.selectedOption === idx;
                    const isCrosswalkMatch =
                        crosswalkMatch != null &&
                        q.answerOptions?.[idx]?.id === crosswalkMatch.optionId;

                    return (
                        <label
                            key={idx}
                            className="flex items-center gap-2 text-sm text-[#53545a] cursor-default"
                        >
                            <input
                                type="radio"
                                name={`user-option-${q.id}`}
                                checked={
                                    isSelected ||
                                    (!hasRealSelection && isCrosswalkMatch)
                                }
                                readOnly
                                className="accent-[#0077b5]"
                            />
                            {opt}
                            {/* {isCrosswalkMatch && (
                                <span className="text-[10px] text-green-600 font-medium ml-1">
                                    (Questionnaire intelligence ·{" "}
                                    {crosswalkMatch.percent}% match)
                                </span>
                            )} */}
                        </label>
                    );
                })}
            </div>

            {/* Confidence */}
            <div className="mt-3 ml-2">
                <p className="text-sm font-semibold text-[#F47F2E] mb-1.5">
                    Confidence
                </p>
                <div className="flex flex-wrap gap-3">
                    {confidenceLevels.map((level) => (
                        <label
                            key={level}
                            className="flex items-center gap-1.5 text-sm text-[#53545a] cursor-default"
                        >
                            <input
                                type="radio"
                                name={`user-confidence-${q.id}`}
                                checked={
                                    q.confidence != null &&
                                    q.confidence === level
                                }
                                readOnly
                                className="accent-[#0077b5]"
                            />
                            {level}
                        </label>
                    ))}
                </div>
            </div>

            {/* ─── Review Status Stamp ─── */}
            {/* {q.reviewStatus && (
        <div className="mt-4 ml-2 flex items-center justify-end">
          {q.reviewStatus === 'approved' && (
            <div className="flex-shrink-0">
              <div className="w-16 h-16 rounded-full border-4 border-[#0077b5] flex items-center justify-center rotate-[-15deg]">
                <p className="text-[8px] font-bold text-[#0077b5] uppercase tracking-wide">Approved</p>
              </div>
            </div>
          )}
          {q.reviewStatus === 'rejected' && (
            <div className="flex-shrink-0">
              <div className="w-16 h-16 rounded-full border-4 border-[#F47F2E] flex items-center justify-center rotate-[15deg]">
                <p className="text-[8px] font-bold text-[#F47F2E] uppercase tracking-wide">Rejected</p>
              </div>
            </div>
          )}
        </div>
      )} */}

            {/* ─── Explanation / Review Section ─── */}
            <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${q.showExplanation
                        ? "max-h-[600px] opacity-100 mt-4"
                        : "max-h-0 opacity-0 mt-0"
                    }`}
            >
                <div className="border-t border-[#919191]/20 pt-4">
                    {rec && (
                        <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-bold text-[#53545a]">
                                Review
                            </h4>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setShowDetails(!showDetails)}
                                    className="px-4 py-1.5 text-xs font-semibold bg-[#0077b5] text-white hover:bg-[#0077b5]/90 transition-colors"
                                >
                                    {showDetails
                                        ? "Hide Details"
                                        : "View Full Details →"}
                                </button>
                                <button
                                    onClick={onToggleExplanation}
                                    className="p-1.5 hover:bg-[#919191]/10 text-[#919191] transition-colors"
                                    title="Close explanation"
                                >
                                    <FiX className="text-sm" />
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Rationale & Cross-Walk */}

                        {rec ? (
                            <div className="bg-[#ffffff] p-4">
                                <div className="flex items-center gap-2 mb-2">
                                    <FiFileText className="text-[#919191]" />
                                    <span className="text-xs font-semibold text-[#919191]">
                                        Rationale & Cross-Walk
                                    </span>
                                </div>
                                <p className="text-xs text-[#53545a] leading-tight mb-3">
                                    {rec?.rationale}
                                </p>

                                {/* Source Mapping Table */}
                                <div className="mt-3 overflow-hidden border border-[#0077b5]/30">
                                    <div className="bg-[#0077b5] text-white text-xs font-bold py-2 px-3 text-center">
                                        Source Mapping
                                    </div>
                                    <div className="divide-y divide-[#919191]/20">
                                        <div className="flex">
                                            <div className="w-28 flex-shrink-0 bg-[#f8f9fa] px-3 py-2 text-[10px] font-bold text-[#919191] uppercase">
                                                Assessment
                                            </div>
                                            <div className="flex-1 px-3 py-2 text-xs text-[#53545a]">
                                                {sourceQuestionData?.assessment_description ||
                                                    "—"}
                                            </div>
                                        </div>
                                        <div className="flex">
                                            <div className="w-28 flex-shrink-0 bg-[#f8f9fa] px-3 py-2 text-[10px] font-bold text-[#919191] uppercase">
                                                Questionnaire
                                            </div>
                                            <div className="flex-1 px-3 py-2 text-xs text-[#53545a]">
                                                {sourceQuestionData?.questionnaire_name ||
                                                    "—"}
                                            </div>
                                        </div>
                                        <div className="flex">
                                            <div className="w-28 flex-shrink-0 bg-[#f8f9fa] px-3 py-2 text-[10px] font-bold text-[#919191] uppercase">
                                                Question
                                            </div>
                                            <div className="flex-1 px-3 py-2 text-xs text-[#53545a]">
                                                {sourceQuestionData?.question_text ||
                                                    "—"}
                                            </div>
                                        </div>
                                        <div className="flex bg-[#E8F5E9] border-l-4 border-[#4CAF50]">
                                            <div className="w-28 flex-shrink-0 bg-[#E8F5E9] px-3 py-2 text-[10px] font-bold text-[#2E7D32] uppercase">
                                                Answer
                                            </div>
                                            <div className="flex-1 px-3 py-2 text-xs text-[#2E7D32] font-semibold">
                                                {sourceAns?.answer_text || "—"}
                                            </div>
                                        </div>
                                        {rec?.evidence_documents?.length >
                                            0 && (
                                                <div className="flex items-center hover:bg-[#f7f8fa] transition-colors">
                                                    <div className="w-28 flex-shrink-0 bg-[#f8f9fa] px-3 py-2 text-[10px] font-bold text-[#919191] uppercase">
                                                        Document
                                                    </div>
                                                    <div className="flex-1 px-3 py-2 flex flex-wrap items-center gap-3">
                                                        {rec.evidence_documents
                                                            .filter(
                                                                (d) => d.blob_url,
                                                            )
                                                            .map((d, i) => (
                                                                <button
                                                                    key={
                                                                        d.ref_doc_id ??
                                                                        d.blob_url ??
                                                                        i
                                                                    }
                                                                    onClick={() =>
                                                                        openEvidenceDoc(
                                                                            d.blob_url,
                                                                        )
                                                                    }
                                                                    title={`Open document: ${d.doc_title || d.blob_url.split("/").pop()}`}
                                                                    className="inline-flex items-center gap-1.5 text-[#0077b5] hover:text-[#005a8f] transition-colors"
                                                                >
                                                                    <FiFile className="text-lg flex-shrink-0" />
                                                                    <span className="text-xs max-w-[180px] truncate">
                                                                        {d.doc_title ||
                                                                            d.blob_url
                                                                                .split(
                                                                                    "/",
                                                                                )
                                                                                .pop()}
                                                                    </span>
                                                                </button>
                                                            ))}
                                                    </div>
                                                </div>
                                            )}
                                        {(q.additionalInfo ||
                                            q.additionalInfoUrl) && (
                                                <div className="flex items-center hover:bg-[#f7f8fa] transition-colors">
                                                    <div className="w-28 flex-shrink-0 bg-[#f8f9fa] px-3 py-2 text-[10px] font-bold text-[#919191] uppercase">
                                                        Evidence -
                                                    </div>
                                                    <div className="flex-1 px-3 py-2 flex items-center gap-2">
                                                        {q.additionalInfo && (
                                                            <span className="text-xs text-[#53545a]">
                                                                {q.additionalInfo}
                                                            </span>
                                                        )}
                                                        {q.additionalInfoUrl && (
                                                            <button
                                                                onClick={
                                                                    handleOpenDocument
                                                                }
                                                                title={`Open: ${q.additionalInfoUrl.split("/").pop()}`}
                                                                className="text-[#0077b5] hover:text-[#005a8f] transition-colors flex-shrink-0"
                                                            >
                                                                <FiFile className="text-lg" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-flex-start gap-3 p-3">
                                <FiAlertCircle
                                    className="text-[#F47F2E] flex-shrink-0"
                                    size={20}
                                />
                                <p className="text-sm font-medium text-[#53545a]">
                                    This answer was chosen by the reviewer
                                    without crosswalk recommendation.
                                </p>
                            </div>
                        )}

                        {/* Reviewer Notes */}
                        <div className="bg-[#ffffff] p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <FiFileText className="text-[#919191]" />
                                <span className="text-xs font-semibold text-[#919191]">
                                    Reviewer Notes
                                </span>
                            </div>
                            <div className="w-full bg-white border border-[#919191] p-3 text-xs text-[#53545a] min-h-[85%]">
                                {q.reviewerNotes || (
                                    <span className="text-[#919191] italic">
                                        write a note...
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ─── Detailed Review Slide-in Panel ─── */}
            <div
                className={`absolute top-0 right-0 h-full w-full sm:w-[280px] md:w-[320px] bg-white border-l border-[#919191] shadow-lg p-3 sm:p-4 transition-transform duration-300 ease-in-out ${showDetails && q.showExplanation ? "translate-x-0" : "translate-x-full"}`}
            >
                <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-bold text-[#53545a]">
                        Detailed Review
                    </h4>
                    <button
                        onClick={() => setShowDetails(false)}
                        className="p-1 hover:bg-[#919191]/10 text-[#919191] transition-colors"
                    >
                        <FiX className="text-sm" />
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex flex-wrap gap-1.5 mb-2 sm:mb-3">
                    {["Cross-Walk", "Provenance", "Audit"].map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setDetailTab(tab)}
                            className={`px-2 sm:px-3 py-1 sm:py-1.5 text-[9px] sm:text-[10px] font-semibold transition-colors ${detailTab === tab
                                    ? "bg-[#F47F2E] text-white"
                                    : "bg-[#919191]/10 text-[#919191] hover:bg-[#919191]/20 border border-[#919191]"
                                }`}
                        >
                            {tab}
                        </button>
                    ))}
                </div>

                {/* Tab content */}
                <div className="text-xs text-[#919191] h-[calc(100%-80px)] overflow-hidden">
                    {detailTab === "Provenance" && (
                        <div>
                            <p className="font-semibold text-[#53545a] mb-1">
                                Provenance:
                            </p>
                        </div>
                    )}

                    {detailTab === "Cross-Walk" && (
                        <div className="h-full flex flex-col">
                            <p className="font-semibold text-[#53545a] mb-1.5 flex-shrink-0">
                                Cross-Walk Details:
                            </p>
                            <style>{`
                .custom-scrollbar {
                  scrollbar-width: thin;
                  scrollbar-color: #0077b5 #f7f8fa;
                }
                .custom-scrollbar::-webkit-scrollbar { width: 8px; }
                .custom-scrollbar::-webkit-scrollbar-track {
                  background: #f7f8fa;
                  border-radius: 10px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                  background: linear-gradient(180deg, #0077b5 0%, #005a8f 100%);
                  border-radius: 10px;
                  border: 2px solid #f7f8fa;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                  background: linear-gradient(180deg, #005a8f 0%, #003d5c 100%);
                }
              `}</style>
                            {rec ? (
                                <div className="custom-scrollbar bg-[#f7f8fa] border border-[#e0e2e6] p-2 flex-1 overflow-y-auto min-h-0">
                                    {rec?.rationale_details ? (
                                        <div className="space-y-2">
                                            {rec.rationale_details
                                                .source_evidence && (
                                                    <div className="bg-white p-2 border border-[#e0e2e6]">
                                                        <p className="text-[10px] sm:text-xs font-semibold text-[#0077b5] mb-1">
                                                            Source Evidence:
                                                        </p>
                                                        <p className="text-[10px] sm:text-xs text-[#53545a] leading-relaxed">
                                                            {
                                                                rec
                                                                    .rationale_details
                                                                    .source_evidence
                                                            }
                                                        </p>
                                                    </div>
                                                )}
                                            {rec.rationale_details
                                                .control_alignment && (
                                                    <div className="bg-white  p-2 border border-[#e0e2e6]">
                                                        <p className="text-[10px] sm:text-xs font-semibold text-[#0077b5] mb-1">
                                                            Control Objective
                                                            Alignment:
                                                        </p>
                                                        <p className="text-[10px] sm:text-xs text-[#53545a] leading-relaxed">
                                                            {
                                                                rec
                                                                    .rationale_details
                                                                    .control_alignment
                                                            }
                                                        </p>
                                                    </div>
                                                )}
                                            {rec.rationale_details
                                                .target_need && (
                                                    <div className="bg-white  p-2 border border-[#e0e2e6]">
                                                        <p className="text-[10px] sm:text-xs font-semibold text-[#0077b5] mb-1">
                                                            Target Need:
                                                        </p>
                                                        <p className="text-[10px] sm:text-xs text-[#53545a] leading-relaxed">
                                                            {
                                                                rec
                                                                    .rationale_details
                                                                    .target_need
                                                            }
                                                        </p>
                                                    </div>
                                                )}
                                            {rec.rationale_details
                                                .org_relevance && (
                                                    <div className="bg-white p-2 border border-[#e0e2e6]">
                                                        <p className="text-[10px] sm:text-xs font-semibold text-[#0077b5] mb-1">
                                                            Organization Relevance:
                                                        </p>
                                                        <p className="text-[10px] sm:text-xs text-[#53545a] leading-relaxed">
                                                            {
                                                                rec
                                                                    .rationale_details
                                                                    .org_relevance
                                                            }
                                                        </p>
                                                    </div>
                                                )}
                                            {rec.rationale_details.caveats && (
                                                <div className="bg-white p-2 border border-[#e0e2e6]">
                                                    <p className="text-[10px] sm:text-xs font-semibold text-[#0077b5] mb-1">
                                                        Caveats:
                                                    </p>
                                                    <p className="text-[10px] sm:text-xs text-[#53545a] leading-relaxed">
                                                        {
                                                            rec
                                                                .rationale_details
                                                                .caveats
                                                        }
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <p className="text-[10px] text-[#919191] italic">
                                            No cross-walk data available.
                                        </p>
                                    )}
                                </div>
                            ) : (
                                <p className="text-[10px] text-[#919191] italic">
                                    No cross-walk proposal for this question.
                                </p>
                            )}
                        </div>
                    )}

                    {detailTab === "Audit" && (
                        <div>
                            <p className="font-semibold text-[#53545a] mb-1">
                                Audit Trail:
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* ─── View Query Chat — slide-in panel (minimized) ─── */}
            <div
                className={`absolute top-0 right-0 h-full w-full sm:w-[340px] md:w-[380px] bg-white border-l border-[#919191] shadow-lg flex flex-col transition-transform duration-300 ease-in-out z-20 ${showChat && !chatMaximized
                        ? "translate-x-0"
                        : "translate-x-full"
                    }`}
            >
                {!chatMaximized && chatBody}
            </div>

            {/* ─── View Query Chat — maximized modal ─── */}
            {showChat && chatMaximized && (
                <div
                    className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
                    onClick={closeChat}
                >
                    <div
                        className="bg-white w-full max-w-3xl h-[85vh] shadow-2xl flex flex-col overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {chatBody}
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserQuestionnaires;
